import { useMemo } from 'react';
import { Box, Chip, Typography } from '@mui/material';
import Section from '../components/Section';
import DataTable from '../components/DataTable';
import useResource from '../components/useResource';

const fmtDate = (iso) => (iso ? String(iso).slice(0, 10) : '—');

const fmtMoney = (v) => {
  const n = Number(v);
  if (isNaN(n)) return '—';
  return n.toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
};

// Autotask Project status picklist (from the project route's documented values).
const PROJECT_STATUS = {
  0: 'Inactive', 1: 'New', 2: 'Implementation', 3: 'Hold — Internal',
  4: 'Administration', 5: 'Complete', 6: 'Scheduled', 8: 'Procurement', 9: 'Prep',
};

// End User Helpdesk tickets live in the Tier 2 Service Desk queue; everything
// else (and tickets with no queue) is treated as internal/operational.
const HELPDESK_QUEUE_ID = 29683481;

/**
 * Activity tab — recent tickets, active projects and open opportunities. Tickets
 * are loaded by the page shell (shared with the header stats) and passed in;
 * projects and opportunities load independently here.
 */
export default function ActivityTab({ code, account, tickets, refreshKey }) {
  const projects = useResource(`autotask/project?status=OpenOnly&account=${encodeURIComponent(code)}`, { refreshKey });

  const hasCompany = !!account?.autotask_id;
  const opportunities = useResource(
    `autotask/opportunity?companyID=${encodeURIComponent(account?.autotask_id || '')}`,
    { enabled: hasCompany, refreshKey },
  );

  const ticketRows  = Array.isArray(tickets?.data) ? tickets.data : [];
  const helpdeskRows = ticketRows.filter(t => Number(t.queueID) === HELPDESK_QUEUE_ID);
  const internalRows = ticketRows.filter(t => Number(t.queueID) !== HELPDESK_QUEUE_ID);
  const projectRows = Array.isArray(projects.data) ? projects.data : [];
  // "Open" opportunities = active status (1); closed/won/lost are filtered out.
  const oppRows = (Array.isArray(opportunities.data) ? opportunities.data : []).filter(o => o.status === 1);

  const ticketColumns = useMemo(() => [
    { accessorKey: 'ticketNumber', header: 'Ticket #', size: 140 },
    { accessorKey: 'title', header: 'Title', size: 320 },
    {
      id: 'status', header: 'Status', size: 140,
      accessorFn: r => r.statusName || r.status || '—',
      Cell: ({ row }) => <Chip size="small" variant="outlined" label={row.original.statusName || row.original.status || '—'} />,
    },
    { id: 'priority', header: 'Priority', size: 100, accessorFn: r => (r.priority ?? '—') },
    { id: 'created', header: 'Created', size: 120, accessorFn: r => fmtDate(r.createDate) },
    { id: 'lastActivity', header: 'Last activity', size: 130, accessorFn: r => fmtDate(r.lastActivityDate) },
  ], []);

  // Internal/operational tickets span many queues, so surface which one.
  const internalTicketColumns = useMemo(() => [
    ticketColumns[0],
    ticketColumns[1],
    { id: 'queue', header: 'Queue', size: 150, accessorFn: r => r.queueName || '—' },
    ...ticketColumns.slice(2),
  ], [ticketColumns]);

  const projectColumns = useMemo(() => [
    { accessorKey: 'projectName', header: 'Project', size: 320 },
    {
      id: 'status', header: 'Status', size: 160,
      accessorFn: r => PROJECT_STATUS[r.status] || r.status || '—',
      Cell: ({ row }) => <Chip size="small" variant="outlined" label={PROJECT_STATUS[row.original.status] || row.original.status || '—'} />,
    },
    { id: 'start', header: 'Start', size: 120, accessorFn: r => fmtDate(r.startDateTime || r.startDate) },
    { id: 'end', header: 'End', size: 120, accessorFn: r => fmtDate(r.endDateTime || r.endDate) },
  ], []);

  const oppColumns = useMemo(() => [
    { accessorKey: 'title', header: 'Opportunity', size: 320 },
    { id: 'amount', header: 'Amount', size: 120, accessorFn: r => fmtMoney(r.amount) },
    {
      id: 'probability', header: 'Probability', size: 110,
      accessorFn: r => (r.probability == null ? '—' : `${r.probability}%`),
    },
    { id: 'close', header: 'Close date', size: 120, accessorFn: r => fmtDate(r.projectedCloseDate || r.closeDate) },
  ], []);

  return (
    <Box>
      <Section title="End User Helpdesk Tickets" count={helpdeskRows.length} loading={tickets?.loading} error={tickets?.error}
        action={<Typography variant="caption" color="text.secondary">Tier 2 Service Desk · open</Typography>}>
        <DataTable columns={ticketColumns} data={helpdeskRows} isLoading={tickets?.loading} emptyText="No open helpdesk tickets." />
      </Section>

      <Section title="Internal & Operational Tickets" count={internalRows.length} loading={tickets?.loading} error={tickets?.error}
        action={<Typography variant="caption" color="text.secondary">All other queues · open</Typography>}>
        <DataTable columns={internalTicketColumns} data={internalRows} isLoading={tickets?.loading} emptyText="No open internal tickets." />
      </Section>

      <Section title="Active Projects" count={projectRows.length} loading={projects.loading} error={projects.error}>
        <DataTable columns={projectColumns} data={projectRows} isLoading={projects.loading} emptyText="No active projects." />
      </Section>

      <Section title="Open Opportunities" count={oppRows.length} loading={opportunities.loading} error={opportunities.error}>
        <DataTable columns={oppColumns} data={oppRows} isLoading={opportunities.loading} emptyText="No open opportunities." />
      </Section>
    </Box>
  );
}
